import { useEffect, useState, useCallback } from 'react';
import { assetHistoryApi, materialUnitApi, siteApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MaterialInfoPanel from '../../components/common/MaterialInfoPanel';
import SearchableSelect from '../../components/common/SearchableSelect';

// ── Material ID history modal — same pattern as InventoryPage.jsx's, ─────────
// duplicated locally per the plan (small, self-contained, not worth a shared
// component refactor).
const HistoryModal = ({ historyModal, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Material History</h3>
          {historyModal !== 'loading' && (
            <p className="text-sm text-gray-500 mt-0.5">{historyModal.unit?.materialIdCode}</p>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
      </div>
      {historyModal === 'loading' ? (
        <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">
            {historyModal.unit?.kit?.name || historyModal.unit?.stationeryItem?.name || historyModal.unit?.itItem?.name} · {historyModal.unit?.site?.name}
          </p>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {historyModal.events.map((e) => (
              <div key={e.id} className="px-3 py-2 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{e.eventType.replace(/_/g, ' ')}</p>
                  {e.remarks && <p className="text-xs text-gray-500 mt-0.5">{e.remarks}</p>}
                  {e.performedByUsername && <p className="text-xs text-gray-400 mt-0.5">by {e.performedByUsername}</p>}
                </div>
                <p className="text-xs text-gray-400 whitespace-nowrap">
                  {new Date(e.occurredAt).toLocaleString('en-GB')}
                </p>
              </div>
            ))}
            {historyModal.events.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">No history recorded yet</p>
            )}
          </div>
          <MaterialInfoPanel materialUnitId={historyModal.unit?.id} unitKitId={historyModal.unit?.kitId} unitItItemId={historyModal.unit?.itItemId} />
        </div>
      )}
    </div>
  </div>
);

// Materials/Stationery/IT split, matching every other inventory module —
// each top-level group has its own set of sections, so a future historical
// category (e.g. a Stationery "Damaged" once that workflow exists) just
// slots into the relevant group's `sections` array without a redesign.
//
// "scrapped" is one shared backend category covering both Stationery and IT
// repair tickets (the server already hides IT rows from callers without
// it_view) — `filterKind` narrows the client-side display to just this
// group's rows rather than needing a second API category.
const TOP_GROUPS = [
  {
    key: 'materials',
    label: 'Materials',
    sections: [
      { key: 'damaged', label: 'Damaged Items', apiCategory: 'damaged' },
      { key: 'validity_over', label: 'Validity Over Items', apiCategory: 'validity_over_material' },
    ],
  },
  {
    key: 'stationery',
    label: 'Stationery',
    sections: [
      { key: 'scrapped', label: 'Scrapped Items', apiCategory: 'scrapped', filterKind: 'STATIONERY' },
      { key: 'validity_over', label: 'Validity Over Items', apiCategory: 'validity_over_stationery' },
    ],
  },
  {
    key: 'it',
    label: 'IT',
    sections: [
      { key: 'scrapped', label: 'Scrapped Items', apiCategory: 'scrapped', filterKind: 'IT' },
      { key: 'validity_over', label: 'Validity Over Items', apiCategory: 'validity_over_it' },
    ],
  },
];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AssetHistory() {
  const { isSiteRestricted: isStorekeeper, hasPermission } = useAuth();
  // The IT group is hidden entirely for callers without it_view — same
  // isolation as the rest of the IT section; asset_history_view alone
  // doesn't imply it.
  const visibleGroups = TOP_GROUPS.filter((g) => g.key !== 'it' || hasPermission('it_view'));

  const [topKey, setTopKey] = useState('materials');
  const [sectionKey, setSectionKey] = useState('damaged');
  const [siteFilter, setSiteFilter] = useState('');
  const [sites, setSites] = useState([]);

  // Time-based filters — same convention as Site History (From/To + quick pills).
  const [fromDate, setFromDate] = useState(() => daysAgoStr(30));
  const [toDate, setToDate] = useState(() => todayStr());

  const [rows, setRows] = useState([]);
  const [unitCodes, setUnitCodes] = useState({}); // materialUnitId -> unit
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [historyModal, setHistoryModal] = useState(null); // { unit, events } | 'loading'

  const activeGroup = visibleGroups.find((g) => g.key === topKey) || visibleGroups[0];
  const activeSection = activeGroup.sections.find((s) => s.key === sectionKey) || activeGroup.sections[0];

  const selectGroup = (key) => {
    setTopKey(key);
    setSectionKey(visibleGroups.find((g) => g.key === key).sections[0].key);
  };

  const applyRange = useCallback((range) => {
    const today = todayStr();
    const map = { '7d': 7, '30d': 30, '3m': 90, '6m': 180, '1y': 365 };
    if (map[range]) { setFromDate(daysAgoStr(map[range])); setToDate(today); return; }
    if (range === 'ytd') { setFromDate(`${new Date().getFullYear()}-01-01`); setToDate(today); }
  }, []);

  // Site dropdown only matters for ALL-access users — ASSIGNED_ONLY users are
  // scoped server-side regardless (see assetHistory.js's resolveScope usage).
  useEffect(() => {
    if (!isStorekeeper) {
      siteApi.list().then((r) => setSites(r.data.data || [])).catch(() => {});
    }
  }, [isStorekeeper]);

  useEffect(() => {
    setLoading(true);
    setError('');
    assetHistoryApi.list(activeSection.apiCategory, { fromDate, toDate })
      .then(async (res) => {
        const data = res.data.data || [];
        setRows(data);
        const allUnitIds = [...new Set(data.flatMap((r) => r.materialUnitIds || []))];
        if (allUnitIds.length === 0) { setUnitCodes({}); return; }
        try {
          const unitsRes = await materialUnitApi.byIds(allUnitIds);
          const map = {};
          (unitsRes.data.data || []).forEach((u) => { map[u.id] = u; });
          setUnitCodes(map);
        } catch { setUnitCodes({}); }
      })
      .catch((err) => setError(err.response?.data?.message || 'Error loading asset history'))
      .finally(() => setLoading(false));
  }, [activeSection.apiCategory, fromDate, toDate]);

  const openMaterialHistory = async (materialUnitId) => {
    setHistoryModal('loading');
    try {
      const res = await materialUnitApi.history(materialUnitId);
      setHistoryModal(res.data.data);
    } catch (err) {
      setHistoryModal(null);
      setError(err.response?.data?.message || 'Error loading material history');
    }
  };

  const displayedRows = rows
    .filter((r) => !activeSection.filterKind || r.kind === activeSection.filterKind)
    .filter((r) => !(!isStorekeeper && siteFilter) || String(r.site?.id) === String(siteFilter));

  return (
    <div className="space-y-5">
      <PageHeader
        title="Discard History"
        subtitle="Items archived out of active tracking, grouped by Materials, Stationery, and IT"
      />

      {/* Top-level Materials / Stationery / IT groups */}
      <div className="flex items-center gap-2 flex-wrap border-b border-slate-200">
        {visibleGroups.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => selectGroup(g.key)}
            className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${
              topKey === g.key
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Sections within the selected group */}
      <div className="flex items-center gap-2 flex-wrap">
        {activeGroup.sections.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSectionKey(s.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              sectionKey === s.key
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 border border-transparent'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Filter Panel — same time-based filtering convention as Site History */}
      <div className="card space-y-3">
        <h2 className="text-sm font-semibold text-slate-700">Filters</h2>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From Date</label>
            <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} className="input w-full text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To Date</label>
            <input type="date" value={toDate} min={fromDate} max={todayStr()} onChange={(e) => setToDate(e.target.value)} className="input w-full text-sm" />
          </div>
          {!isStorekeeper && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Site</label>
              <SearchableSelect
                className="input w-full text-sm"
                options={[{ id: '', label: 'All Sites' }, ...sites.map((s) => ({ id: s.id, label: s.name }))]}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder="All Sites"
              />
            </div>
          )}
        </div>

        {/* Quick range pills — directly tied to the date inputs above */}
        <div className="flex items-center gap-2 flex-wrap pb-0.5 border-b border-slate-100">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mr-1">Quick Select:</span>
          {[{ l: '7D', v: '7d' }, { l: '30D', v: '30d' }, { l: '3M', v: '3m' }, { l: '6M', v: '6m' }, { l: '1Y', v: '1y' }, { l: 'YTD', v: 'ytd' }].map(({ l, v }) => {
            const today = todayStr();
            const active = (() => {
              if (toDate !== today) return false;
              if (v === '7d') return fromDate === daysAgoStr(7);
              if (v === '30d') return fromDate === daysAgoStr(30);
              if (v === '3m') return fromDate === daysAgoStr(90);
              if (v === '6m') return fromDate === daysAgoStr(180);
              if (v === '1y') return fromDate === daysAgoStr(365);
              if (v === 'ytd') return fromDate === `${new Date().getFullYear()}-01-01`;
              return false;
            })();
            return (
              <button key={v} type="button" onClick={() => applyRange(v)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 border border-transparent'
                }`}>
                {l}
              </button>
            );
          })}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? <LoadingSpinner /> : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Material Name</th>
                <th>Material ID</th>
                <th>Site</th>
                <th>Held By</th>
                <th>Date Archived</th>
                <th>Current Status</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map((r) => {
                const codes = (r.materialUnitIds || []).map((id) => unitCodes[id]?.materialIdCode).filter(Boolean);
                return (
                  <tr key={r.id}>
                    <td className="font-medium text-sm">{r.materialName}</td>
                    <td>
                      {codes.length > 0 ? codes.map((code, i) => {
                        const unitId = r.materialUnitIds[i];
                        return (
                          <button
                            key={unitId}
                            className="font-mono text-xs text-indigo-600 hover:underline mr-1"
                            onClick={() => openMaterialHistory(unitId)}
                          >
                            {code}
                          </button>
                        );
                      }) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="text-sm">{r.site?.name || '—'}</td>
                    <td className="text-sm">
                      {r.heldBy
                        ? <>{r.heldBy.name}{r.heldBy.employeeId ? ` (${r.heldBy.employeeId})` : ''}</>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="text-gray-400 text-xs">{r.dateArchived ? new Date(r.dateArchived).toLocaleDateString('en-GB') : '—'}</td>
                    <td>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200">
                        {r.currentStatus}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {displayedRows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-gray-400">No items to show for the selected filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {historyModal && (
        <HistoryModal historyModal={historyModal} onClose={() => setHistoryModal(null)} />
      )}
    </div>
  );
}
